import { useMemo, useState, useSyncExternalStore } from "react";
import { Link, useParams } from "react-router-dom";
import { Art, Notice } from "../components/Cards";
import * as I from "../components/Icons";
import { allEpisodes, findTitle, languageName, runtimeLabel } from "../lib/catalog";
import { checkMedia } from "../player/capabilities";
import { getProgress, lastWatched, subscribe } from "../services/progress";
import { useCatalog } from "../state/CatalogContext";
import type { Media } from "../types";

const CAT = { movie: "Película", series: "Serie", anime: "Anime" } as const;

function trackSummary(m: Media | undefined) {
  if (!m) return { audio: [], subs: [] as string[] };
  const audio = [...new Set(m.audio.filter((a) => !a.commentary).map((a) => a.label || languageName(a.language)))];
  const subs = [...new Set(m.subtitles.map((s) => s.label || languageName(s.language)))];
  return { audio, subs };
}

export function TitlePage() {
  const { id } = useParams();
  const { catalog } = useCatalog();
  useSyncExternalStore(subscribe, () => lastWatched(id ?? "")?.at ?? 0);
  const t = findTitle(catalog!, id ?? "");
  const eps = useMemo(() => (t ? allEpisodes(t) : []), [t]);
  const last = t ? lastWatched(t.id) : undefined;
  const [season, setSeason] = useState<number | null>(null);
  if (!t) return <Notice title="No encontramos este título" action={<Link className="btn" to="/">Ir al inicio</Link>}>Puede que se haya retirado del catálogo.</Notice>;

  const seasons = t.seasons ?? [];
  const curSeason = season ?? (last?.episodeId ? eps.find((e) => e.id === last.episodeId)?.season : undefined) ?? seasons[0]?.number;
  const m = t.media ?? eps[0]?.media;
  const { audio, subs } = trackSummary(m);
  const compat = m ? checkMedia(m) : null;
  const resumeEp = last?.episodeId ? eps.find((e) => e.id === last.episodeId) : undefined;
  const playTo = resumeEp ? `/ver/${t.id}/${resumeEp.id}` : t.kind === "series" ? `/ver/${t.id}/${eps[0]?.id}` : `/ver/${t.id}`;
  const v = m?.video[0];

  return (
    <div className="pb-20">
      <section className="relative isolate min-h-[64vh] overflow-hidden">
        <Art k={t.backdrop ?? t.poster} alt="" eager className="absolute inset-0 -z-10 h-full w-full" />
        <div className="beam absolute inset-0 -z-10" />
        <div className="mx-auto flex min-h-[64vh] max-w-[1800px] flex-col justify-end px-4 pb-10 pt-28 sm:px-8 lg:px-12">
          <p className="text-sm text-muted">{CAT[t.category]}</p>
          <h1 className="hero-title mt-1 max-w-4xl">{t.title}</h1>
          {t.originalTitle && t.originalTitle !== t.title && <p className="mt-2 font-display text-xl italic text-text/70">{t.originalTitle}</p>}
          <p className="mt-4 flex flex-wrap gap-x-4 text-[15px] text-text/85">
            {t.year && <span>{t.year}</span>}
            {t.ageRating && <span className="rounded border border-line px-1.5 text-sm">{t.ageRating}</span>}
            {t.kind === "movie" && m && <span>{runtimeLabel(m.durationSec)}</span>}
            {t.kind === "series" && <span>{seasons.length} temporada{seasons.length === 1 ? "" : "s"}, {eps.length} episodios</span>}
            {v && <span>{v.height >= 2000 ? "4K" : `${v.height}p`}{v.range && v.range !== "SDR" ? " HDR" : ""}</span>}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={playTo} className="btn btn-primary px-6 py-3 text-[17px]" data-testid="play-title">
              <I.Play width={20} height={20} />
              {resumeEp ? `Continuar T${resumeEp.season} E${resumeEp.number}` : last ? "Continuar" : "Reproducir"}
            </Link>
          </div>
          {compat?.message && <p className="mt-4 max-w-2xl rounded-lg border border-accent-2/40 bg-accent-2/10 px-4 py-3 text-sm" role="note">{compat.message}</p>}
        </div>
      </section>

      <div className="mx-auto mt-8 grid max-w-[1800px] gap-10 px-4 sm:px-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:px-12">
        <div>
          {t.overview && <p className="max-w-[68ch] text-[17px] leading-relaxed text-text/85">{t.overview}</p>}
          {t.kind === "series" && (
            <section className="mt-10" aria-label="Episodios">
              <div className="flex items-center gap-4">
                <h2 className="font-display text-2xl">Episodios</h2>
                {seasons.length > 1 && (
                  <select value={curSeason} onChange={(e) => setSeason(Number(e.target.value))} className="rounded-md border border-line bg-surface px-3 py-1.5" aria-label="Temporada">
                    {seasons.map((s) => <option key={s.number} value={s.number}>{s.title || `Temporada ${s.number}`}</option>)}
                  </select>
                )}
              </div>
              <ol className="mt-4 divide-y divide-line/70">
                {seasons.find((s) => s.number === curSeason)?.episodes.map((e) => {
                  const p = getProgress(t.id, e.id);
                  const frac = p && p.dur ? p.pos / p.dur : 0;
                  return (
                    <li key={e.id}>
                      <Link to={`/ver/${t.id}/${e.id}`} className="group flex gap-4 py-4" data-testid="episode">
                        <span className="w-8 shrink-0 pt-1 text-right font-display text-2xl text-muted">{e.number}</span>
                        <div className="relative aspect-video w-36 shrink-0 overflow-hidden rounded-md bg-surface sm:w-48">
                          <Art k={e.still ?? t.backdrop} alt="" className="h-full w-full" />
                          <span className="absolute inset-0 grid place-items-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100"><I.Play /></span>
                          {frac > 0 && <span className="absolute inset-x-0 bottom-0 h-1 bg-black/60"><span className="block h-full bg-accent" style={{ width: `${Math.min(100, frac * 100)}%` }} /></span>}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium">{e.title}</p>
                          <p className="text-sm text-muted">{runtimeLabel(e.media.durationSec)}</p>
                          {e.overview && <p className="mt-1 line-clamp-2 text-sm text-text/70">{e.overview}</p>}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}
        </div>
        <aside className="space-y-5 text-sm">
          {t.genres?.length ? <Fact label="Géneros">{t.genres.join(", ")}</Fact> : null}
          {audio.length > 0 && <Fact label="Audio">{audio.join(", ")}</Fact>}
          <Fact label="Subtítulos">{subs.length ? subs.join(", ") : "No disponibles"}</Fact>
          {m?.chapters?.length ? <Fact label="Capítulos">{m.chapters.length}</Fact> : null}
        </aside>
      </div>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-muted">{label}</p>
      <p className="mt-0.5 text-text/90">{children}</p>
    </div>
  );
}
